import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ChevronDown, ChevronRight, ArrowLeft, Loader2, FolderTree, Package,
  Ruler, FileDown, FileText, Info, FileSpreadsheet,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Bc3Viewer() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? (localStorage.getItem("ap_token") || "") : "";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openChap, setOpenChap] = useState({});
  const [openPart, setOpenPart] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: tree } = await api.get(`/bc3/projects/${fileId}/tree`);
        if (cancelled) return;
        setData(tree);
        const map = {};
        (tree.chapters || []).filter((c) => c.depth === 0).forEach((c) => { map[c.id] = true; });
        setOpenChap(map);
      } catch (err) {
        const msg = err?.response?.data?.detail || "No se pudo cargar el árbol BC3";
        toast.error(msg);
        if (err?.response?.status === 404) {
          setTimeout(() => navigate(`/files`), 1500);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fileId, navigate]);

  const rootNodes = useMemo(() => {
    if (!data) return [];
    const chapters = data.chapters || [];
    const partidas = data.partidas || [];
    const chapMap = {};
    chapters.forEach((c) => {
      chapMap[c.id] = { ...c, kind: "chapter", children: [], partidas: [] };
    });
    const roots = [];
    chapters.forEach((c) => {
      if (c.parent_chapter_id && chapMap[c.parent_chapter_id]) {
        chapMap[c.parent_chapter_id].children.push(chapMap[c.id]);
      } else {
        roots.push(chapMap[c.id]);
      }
    });
    const orphans = [];
    partidas.forEach((p) => {
      const target = p.chapter_id ? chapMap[p.chapter_id] : null;
      const node = { ...p, kind: "partida", mediciones: (data.mediciones_by_partida || {})[p.id] || [] };
      if (target) target.partidas.push(node); else orphans.push(node);
    });
    if (orphans.length) {
      roots.push({ id: "_orphans", kind: "chapter", codigo: "—", titulo: "Partidas sin capítulo", depth: 0, children: [], partidas: orphans });
    }
    return roots;
  }, [data]);

  const totalImporte = useMemo(() => {
    if (!data) return 0;
    return (data.partidas || []).reduce((s, p) => s + (p.total || 0), 0);
  }, [data]);

  const toggleChap = (id) => setOpenChap((m) => ({ ...m, [id]: m[id] === false ? true : !(m[id] ?? true) }));
  const togglePart = (id) => setOpenPart((m) => ({ ...m, [id]: !m[id] }));

  const renderChapter = (node, depth) => {
    const isOpen = openChap[node.id] !== false;
    const hasContent = (node.children?.length || 0) + (node.partidas?.length || 0) > 0;
    return (
      <div key={node.id} className="border-b border-slate-100 last:border-b-0">
        <button
          type="button"
          onClick={() => hasContent && toggleChap(node.id)}
          className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 text-left"
          style={{ paddingLeft: 16 + depth * 24 }}
          data-testid={`bc3-chapter-${node.id}`}
        >
          <span className="w-5">
            {hasContent ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
          </span>
          <FolderTree className="w-4 h-4 text-orange-600" />
          <span className="font-mono text-xs text-slate-500">{node.codigo}</span>
          <span className="font-bold text-sm flex-1 truncate">{node.titulo || "(sin título)"}</span>
          <span className="text-xs text-slate-400">
            {node.children?.length ? `${node.children.length} cap · ` : ""}
            {node.partidas?.length ? `${node.partidas.length} part.` : ""}
          </span>
        </button>
        {isOpen && (
          <div>
            {(node.children || []).map((c) => renderChapter(c, depth + 1))}
            {(node.partidas || []).map((p) => renderPartida(p, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderPartida = (node, depth) => {
    const isOpen = openPart[node.id] === true;
    const hasDetail = !!node.descripcion || (node.mediciones?.length || 0) > 0;
    return (
      <div key={node.id} className="border-t border-slate-100">
        <button
          type="button"
          onClick={() => hasDetail && togglePart(node.id)}
          className="w-full flex items-start gap-2 px-4 py-3 hover:bg-slate-50 text-left"
          style={{ paddingLeft: 16 + depth * 24 }}
          data-testid={`bc3-partida-${node.id}`}
        >
          <span className="w-5 mt-0.5">
            {hasDetail ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
          </span>
          <Package className="w-4 h-4 text-slate-500 mt-0.5" />
          <span className="font-mono text-xs text-slate-500 mt-1 whitespace-nowrap">{node.codigo}</span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{node.titulo || "(sin título)"}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {(node.cantidad ?? 1).toLocaleString("es-ES")} × {node.unidad || "ud"} · {(node.precio ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/ud
              {node.mediciones_total != null && (
                <> · <span className="text-slate-400">medición: {node.mediciones_total.toLocaleString("es-ES")}</span></>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="font-bold text-sm tabular-nums">{(node.total || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
          </div>
        </button>
        {isOpen && hasDetail && (
          <div className="bg-slate-50 border-t border-slate-100 px-4 py-3" style={{ paddingLeft: 16 + (depth + 1) * 24 + 20 }} data-testid={`bc3-partida-detail-${node.id}`}>
            {node.descripcion && (
              <div className="mb-3">
                <div className="kbar mb-1.5 inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Descripción</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{node.descripcion}</div>
              </div>
            )}
            {node.mediciones?.length > 0 && renderMedicionesTable(node)}
          </div>
        )}
      </div>
    );
  };

  const renderMedicionesTable = (node) => (
    <div>
      <div className="kbar mb-1.5 inline-flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Mediciones</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 bg-white">
          <thead className="bg-white">
            <tr className="text-left">
              <th className="p-2 border-b border-slate-200 font-semibold">Descripción</th>
              <th className="p-2 border-b border-slate-200 font-semibold text-right w-16">Ud.</th>
              <th className="p-2 border-b border-slate-200 font-semibold text-right w-20">Longitud</th>
              <th className="p-2 border-b border-slate-200 font-semibold text-right w-20">Anchura</th>
              <th className="p-2 border-b border-slate-200 font-semibold text-right w-20">Altura</th>
              <th className="p-2 border-b border-slate-200 font-semibold text-right w-24">Parcial</th>
            </tr>
          </thead>
          <tbody>
            {node.mediciones.map((m, i) => {
              const rowClass = m.tipo === "2" ? "bg-amber-50 font-semibold" : m.tipo === "3" ? "bg-slate-100 font-bold" : "";
              return (
                <tr key={m.id || i} className={"border-b border-slate-100 " + rowClass}>
                  <td className="p-2">{m.comentario || (m.tipo === "2" ? "Subtotal" : m.tipo === "3" ? "Total" : "—")}</td>
                  <td className="p-2 text-right tabular-nums">{m.unidades ?? "—"}</td>
                  <td className="p-2 text-right tabular-nums">{m.longitud ?? "—"}</td>
                  <td className="p-2 text-right tabular-nums">{m.anchura ?? "—"}</td>
                  <td className="p-2 text-right tabular-nums">{m.altura ?? "—"}</td>
                  <td className="p-2 text-right tabular-nums font-semibold">{(m.parcial ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              );
            })}
            {node.mediciones_total != null && (
              <tr className="bg-slate-900 text-white">
                <td className="p-2 font-bold" colSpan={5}>Total medición</td>
                <td className="p-2 text-right tabular-nums font-bold">
                  {node.mediciones_total.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {node.unidad || ""}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-slate-500 p-12" data-testid="bc3-viewer-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando árbol BC3…
      </div>
    );
  }
  if (!data) return null;

  const p = data.project || {};

  return (
    <div className="space-y-6" data-testid="bc3-viewer-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="bc3-viewer-back">
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
          <div className="kbar">Visor BC3</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-1" data-testid="bc3-viewer-title">
            {p.title || "Proyecto BC3"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API_BASE}/bc3/projects/${fileId}/xlsx?ap_token=${token}`}
            className="h-11 px-4 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 inline-flex items-center gap-2 text-sm font-semibold"
            data-testid="bc3-viewer-xlsx-btn"
            title="Descargar toda la estructura en Excel"
          >
            <FileSpreadsheet className="w-4 h-4" /> Exportar Excel
          </a>
          <Link
            to={`/bc3-import?file_id=${fileId}`}
            className="h-11 px-5 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white inline-flex items-center gap-2 text-sm font-semibold"
            data-testid="bc3-viewer-import-btn"
          >
            <FileDown className="w-4 h-4" /> Importar a presupuesto
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        <div className="bg-white p-4" data-testid="bc3-sum-chapters">
          <div className="kbar flex items-center gap-2"><FolderTree className="w-4 h-4" />Capítulos</div>
          <div className="font-display font-extrabold text-2xl mt-1 tracking-tight">{p.chapter_count || 0}</div>
        </div>
        <div className="bg-white p-4" data-testid="bc3-sum-partidas">
          <div className="kbar flex items-center gap-2"><Package className="w-4 h-4" />Partidas</div>
          <div className="font-display font-extrabold text-2xl mt-1 tracking-tight">{p.partida_count || 0}</div>
        </div>
        <div className="bg-white p-4" data-testid="bc3-sum-med">
          <div className="kbar flex items-center gap-2"><Ruler className="w-4 h-4" />Mediciones</div>
          <div className="font-display font-extrabold text-2xl mt-1 tracking-tight">{p.mediciones_count || 0}</div>
        </div>
        <div className="bg-white p-4" data-testid="bc3-sum-total">
          <div className="kbar">Importe total</div>
          <div className="font-display font-extrabold text-2xl mt-1 tracking-tight">
            {totalImporte.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
        </div>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="bc3-viewer-tree">
        {rootNodes.length === 0 && (
          <div className="p-12 text-center text-slate-500">Este BC3 no contiene capítulos ni partidas legibles.</div>
        )}
        {rootNodes.map((n) => renderChapter(n, 0))}
      </div>

      <p className="text-xs text-slate-400 inline-flex items-center gap-1.5">
        <Info className="w-3.5 h-3.5" />
        Datos leídos desde MongoDB (colecciones bc3_projects / bc3_chapters / bc3_partidas / bc3_mediciones).
      </p>
    </div>
  );
}
